// @ts-check

/**
 * @file Renders the project board column create/edit modal.
 */

import {Form} from "@kaspernj/api-maker/build/form"
import FontAwesome6 from "@expo/vector-icons/FontAwesome6"
import _ from "gettext-universal/build/src/translate.js"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"
import {createRef, memo, useEffect} from "react"
import PropTypes from "prop-types"
import PropTypesExact from "prop-types-exact"
import {Pressable, Text, View} from "react-native-propforge"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component.js"
import FormTextInput from "../../../components/form/form-text-input"
import Modal from "../../../components/modal"
import {BoardColumn} from "../../../frontend-models/board-column"
import errorMessage from "../../../shared/error-message"
import useThemeColors, {useThemeName} from "../../../shared/use-theme-colors"

/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @type {Record<string, object>} */
const styles = {}
const COLOR_OPTIONS = [
  "#64748b",
  "#2563eb",
  "#16a34a",
  "#f59e0b",
  "#f43f5e",
  "#a855f7",
  "#06b6d4"
]

/** @typedef {(...args: unknown[]) => unknown | Promise<unknown>} ProjectBoardNewColumnModalCallback */

/**
 * @typedef {object} ProjectBoardNewColumnModalProps
 * @property {string | undefined} boardId Board id for new columns.
 * @property {BoardColumn | null | undefined} column Column being edited.
 * @property {number} currentColumnCount Current board column count.
 * @property {boolean} isVisible Whether the modal is visible.
 * @property {ProjectBoardNewColumnModalCallback} onClosePress Close callback.
 * @property {ProjectBoardNewColumnModalCallback | undefined} onCreated Created callback.
 * @property {ProjectBoardNewColumnModalCallback | undefined} onUpdated Updated callback.
 */

/**
 * @typedef {object} ProjectBoardNewColumnModalState
 * @property {string} errorText Validation or save error text.
 * @property {boolean} saving Whether a save request is running.
 * @property {string} selectedColor Selected column color.
 */

/**
 * Project board create-column modal.
 * @augments {ShapeComponent<ProjectBoardNewColumnModalProps, ProjectBoardNewColumnModalState>}
 */
class ProjectBoardNewColumnModal extends ShapeComponent {
  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()
  formObjectRef = createRef()
  static propTypes = PropTypesExact({
    boardId: PropTypes.string,
    column: PropTypes.object,
    currentColumnCount: PropTypes.number.isRequired,
    isVisible: PropTypes.bool.isRequired,
    onClosePress: PropTypes.func.isRequired,
    onCreated: PropTypes.func,
    onUpdated: PropTypes.func
  })
  static defaultProps = {
    boardId: undefined,
    column: null,
    onCreated: undefined,
    onUpdated: undefined
  }
  /** @type {ProjectBoardNewColumnModalState} */
  state = {
    errorText: "",
    saving: false,
    selectedColor: COLOR_OPTIONS[1]
  }

  /** Initializes locale handling and modal state resets. */
  setup() {
    useLocale()
    this.colors = useThemeColors()
    this.themeName = useThemeName()
    /** @type {BoardColumn | null} */
    const column = this.p.column
    const isVisible = this.p.isVisible
    const initialName = column?.name() || ""
    const initialColor = column?.color() || COLOR_OPTIONS[1]
    useEffect(() => {
      if (!isVisible) return
      this.s.errorText = ""
      this.s.saving = false
      this.s.selectedColor = initialColor
    }, [initialColor, initialName, isVisible])
  }

  /**
   * Renders the column modal.
   * @returns {ReactNode} Column modal content.
   */
  render() {
    const initialName = this.p.column?.name() || ""
    const colors = this.tt.colors
    const themeName = this.tt.themeName
    return (
      <Modal
        cardStyle={styles[`card-${themeName}`] ||= {
          gap: 16,
          maxWidth: 420,
          padding: 20
        }}
        isVisible={this.p.isVisible}
        onRequestClose={this.p.onClosePress}
        testID="projectBoardNewColumnModal"
      >
          <Text
            style={styles[`title-${themeName}`] ||= {
              color: colors.text,
              fontSize: 20,
              fontWeight: 700
            }}
            testID="projectBoardNewColumnModal/title"
          >
            {this.p.column ? _("Edit Column") : _("New Column")}
          </Text>
          <Form
            formObjectRef={this.formObjectRef}
            onSubmit={this.tt.onSavePress}
          >
            <View
              style={styles.field ||= {
                gap: 6
              }}
              testID="projectBoardNewColumnModal/nameField"
            >
              <FormTextInput
                defaultValue={initialName}
                label={_("Name")}
                name="name"
                testID="projectBoardNewColumnModal/nameInput"
              />
            </View>
            <View
              style={styles.colorSection ||= {
                gap: 8
              }}
              testID="projectBoardNewColumnModal/colorSection"
            >
              <Text
                style={styles[`label-${themeName}`] ||= {
                  color: colors.textMuted,
                  fontSize: 12,
                  fontWeight: 700,
                  textTransform: "uppercase"
                }}
                testID="projectBoardNewColumnModal/colorLabel"
              >
                {_("Status color")}
              </Text>
              <View
                style={styles.colorRow ||= {
                  flexDirection: "row",
                  flexWrap: "wrap",
                  gap: 8
                }}
                testID="projectBoardNewColumnModal/colorOptions"
              >
                {COLOR_OPTIONS.map((colorValue) => (
                  <Pressable
                    key={colorValue}
                    onPress={() => this.tt.onColorPress(colorValue)}
                    style={styles[`colorChip-${themeName}-${colorValue === this.s.selectedColor}-${colorValue}`] ||= {
                      alignItems: "center",
                      backgroundColor: colors.surface,
                      borderColor:
                        colorValue === this.s.selectedColor
                          ? colors.focusRing
                          : colors.borderStrong,
                      borderRadius: 999,
                      borderWidth: 2,
                      height: 32,
                      justifyContent: "center",
                      width: 32
                    }}
                    testID={`projectBoardNewColumnModal/color-${colorValue.replace("#", "")}`}
                  >
                    <View
                      style={styles[`colorSwatch-${colorValue}`] ||= {
                        backgroundColor: colorValue,
                        borderRadius: 999,
                        height: 18,
                        width: 18
                      }}
                      testID={`projectBoardNewColumnModal/color-${colorValue.replace("#", "")}/swatch`}
                    />
                  </Pressable>
                ))}
              </View>
            </View>
            {this.s.errorText !== "" &&
              <View
                style={styles[`errorCard-${themeName}`] ||= {
                  backgroundColor: colors.dangerBackground,
                  borderColor: colors.danger,
                  borderRadius: 12,
                  borderWidth: 1,
                  padding: 12
                }}
                testID="projectBoardNewColumnModal/error"
              >
                <Text
                  style={styles[`errorText-${themeName}`] ||= {
                    color: colors.dangerText,
                    fontSize: 14,
                    fontWeight: 600
                  }}
                  testID="projectBoardNewColumnModal/errorText"
                >
                  {this.s.errorText}
                </Text>
              </View>
            }
            <View
              style={styles.actions ||= {
                flexDirection: "row",
                gap: 10,
                justifyContent: "flex-end"
              }}
              testID="projectBoardNewColumnModal/actions"
            >
              <Pressable
                onPress={this.p.onClosePress}
                style={styles[`cancelButton-${themeName}`] ||= {
                  alignItems: "center",
                  backgroundColor: colors.surfaceMuted,
                  borderColor: colors.border,
                  borderRadius: 10,
                  borderWidth: 1,
                  flexDirection: "row",
                  paddingHorizontal: 14,
                  paddingVertical: 10
                }}
                testID="projectBoardNewColumnModal/cancelButton"
              >
                <View
                  style={styles.cancelButtonIcon ||= {
                    marginRight: 8,
                    width: 14
                  }}
                  testID="projectBoardNewColumnModal/cancelButton/icon"
                >
                  <FontAwesome6 color={colors.text} name="xmark" size={14} />
                </View>
                <Text
                  style={styles[`cancelText-${themeName}`] ||= {
                    color: colors.text,
                    fontSize: 14,
                    fontWeight: 600
                  }}
                  testID="projectBoardNewColumnModal/cancelButton/text"
                >
                  {_("Cancel")}
                </Text>
              </Pressable>
              <Pressable
                onPress={this.tt.onSavePress}
                style={styles[`createButton-${themeName}`] ||= {
                  alignItems: "center",
                  backgroundColor: colors.primary,
                  borderRadius: 10,
                  flexDirection: "row",
                  paddingHorizontal: 14,
                  paddingVertical: 10
                }}
                testID="projectBoardNewColumnModal/createButton"
              >
                <View
                  style={styles.createButtonIcon ||= {
                    marginRight: 8,
                    width: 14
                  }}
                  testID="projectBoardNewColumnModal/createButton/icon"
                >
                  <FontAwesome6 color={colors.onPrimary} name="plus" size={14} />
                </View>
                <Text
                  style={styles[`createText-${themeName}`] ||= {
                    color: colors.onPrimary,
                    fontSize: 14,
                    fontWeight: 600
                  }}
                  testID="projectBoardNewColumnModal/createButton/text"
                >
                  {this.s.saving
                    ? this.p.column
                      ? _("Saving...")
                      : _("Creating...")
                    : this.p.column
                      ? _("Save")
                      : _("Create")}
                </Text>
              </Pressable>
            </View>
          </Form>
      </Modal>
    )
  }

  /**
   * Selects the column color.
   * @param {string} colorValue Color value to select.
   * @returns {void}
   */
  onColorPress = (colorValue) => {
    this.s.errorText = ""
    this.s.selectedColor = colorValue
  }

  /**
   * Saves a new or existing board column.
   * @returns {Promise<void>} Resolves when save handling completes.
   */
  onSavePress = async () => {
    const boardId = this.p.boardId
    /** @type {BoardColumn | null} */
    const column = this.p.column
    const formObject = this.formObjectRef.current
    if (!formObject) {
      return
    }
    const values = formObject.asObject()
    const trimmedName = String(values.name || "").trim()
    if (!trimmedName) {
      this.s.errorText = _("Please enter a column name.")
      return
    }
    if (!boardId) {
      this.s.errorText = _("Board not found.")
      return
    }
    this.s.errorText = ""
    this.s.saving = true
    try {
      if (column) {
        await column.update({
          color: this.s.selectedColor,
          name: trimmedName
        })
        await this.p.onUpdated?.(column)
        return
      }
      const boardColumn = new BoardColumn()
      boardColumn.setBoardId(boardId)
      boardColumn.setColor(this.s.selectedColor)
      boardColumn.setName(trimmedName)
      boardColumn.setPosition(this.p.currentColumnCount + 1)
      await boardColumn.save()
      await this.p.onCreated?.(boardColumn)
    } catch (error) {
      this.s.errorText = errorMessage(error)
      this.s.saving = false
    }
  }
}

/** @type {NamedExoticComponent<ProjectBoardNewColumnModalProps>} */
const ProjectBoardNewColumnModalComponent = memo(shapeComponent(ProjectBoardNewColumnModal))

export default ProjectBoardNewColumnModalComponent
